/// <amd-module name="Repos/PullRequest/Attachments/Components/AttachmentsDropTarget" />
import * as React from "react";
import * as Resources from "Repos/PullRequest/Attachments/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { equals } from "VSS/Core/Util/String";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";

export interface IAttachmentsDropTargetProps {
    /**
     * Called when the files are dropped to the drop target.
     */
    onFilesDrop: (event: React.DragEvent<HTMLDivElement>) => void;

    /**
     * Overlay message to display when the files are dragged into the drop target.
     * @default "Drop files here"
     */
    overlayMessage?: React.ReactNode;
}

/**
 * This component makes any specified content a drop target for files by attaching
 * necessary drag events and displaying an overlay when the dragged files are in within the target.
 */
export const AttachmentsDropTarget: React.FunctionComponent<IAttachmentsDropTargetProps> = props => {
    const overlayVisible = new ObservableValue(false);

    const draggingFiles = (event: React.DragEvent<HTMLDivElement>): boolean => {
        let hasFiles = false;
        if (event.dataTransfer && event.dataTransfer.types) {
            const { types } = event.dataTransfer;
            for (let i = 0; i < types.length; i++) {
                if (equals(types[i], "files", true)) {
                    hasFiles = true;
                    break;
                }
            }
        }

        return hasFiles;
    };

    const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        // Make sure we display overlay if the dragged objects are files only
        if (draggingFiles(event)) {
            overlayVisible.value = true;
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        // We only hide overlay when the mouse leaves the actual drop target
        // (not any off the children since things get messy in that case)
        if (draggingFiles(event) && event.currentTarget === event.target) {
            overlayVisible.value = false;
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        // This is required to get onDrop event fired
        event.preventDefault();
        event.stopPropagation();
    };

    const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
        if (draggingFiles(event)) {
            overlayVisible.value = false;
            event.preventDefault();
            event.stopPropagation();

            if (props.onFilesDrop) {
                props.onFilesDrop(event);
            }
        }
    };

    return (
        <div
            className="repos-attachments-drop-target relative"
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
        >
            <Observer visible={overlayVisible}>
                {(overlayProps: { visible: boolean }) => {
                    return (
                        <>
                            <div className={overlayProps.visible ? "no-events" : undefined}>{props.children}</div>
                            <div
                                className={css(
                                    "repos-attachments-drop-overlay absolute-fill no-events",
                                    overlayProps.visible && "flex",
                                    !overlayProps.visible && "hidden"
                                )}
                            >
                                <div className="flex justify-center flex-grow font-size-l font-weight-semibold margin-8">
                                    {props.overlayMessage || Resources.AttachmentDropMessage}
                                </div>
                            </div>
                        </>
                    );
                }}
            </Observer>
        </div>
    );
};
